import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from '../../theme/swal';
import { IcBuilding, IcPercent, IcBanknote, IcUsers, IcContact, IcLogOut, IcSave, IcImage, IcPlus, IcTrash, IcPencil, IcSparkles, IcTags, IcLayers, IcSettings, IcMapPin } from '../../theme/icons';
import companyService from '../../services/companyService';
import chargeService from '../../services/chargeService';
import authService from '../../services/authService';
import { useCompany } from '../../context/CompanyContext';
import KI from '../../theme/palette';
// Pages intégrées dans Paramètres (owner 2026-09-08 : plus de changement de
// page — Services, Catégories clients, Catégories produits et Utilisateurs
// s'affichent à droite du rail, comme Identité / Charges).
import ServicesList from '../services/ServicesList';
import ClientCategoryManagement from '../customers/ClientCategoryManagement';
import CategoryList from '../categories/CategoryList';
import UserList from '../UserManagement/UserList';
import PeopleList from '../UserManagement/PeopleList';
import BoutiquesList from '../UserManagement/BoutiquesList';
import SectionCard from '../../components/SectionCard';
import { CardSkeleton, TableSkeleton } from '../../components/Skeleton';

// Formulaire (Brand Kit §05) : champ .inp, libellé 500 encre — même
// grammaire que AddCustomer / AddProduct / ServiceForm.
const inputClass = 'inp w-full px-3.5 py-2.5 rounded-xl text-base text-gray-900';
const labelClass = 'block text-sm font-medium text-gray-900 mb-1.5';

const round3 = (n) => Math.round((n + Number.EPSILON) * 1000) / 1000;

const CHARGE_TYPES = [
  { value: 'MONTHLY', label: 'Mensuelle' },
  { value: 'TRIMESTER', label: 'Trimestrielle' },
  { value: 'SEMESTER', label: 'Semestrielle' },
  { value: 'ANNUAL', label: 'Annuelle' },
];

// Rail de Paramètres (refonte 2026-09-08) : onglets groupés par domaine.
const TAB_GROUPS = [
  { label: 'Entreprise', tabs: [
    { key: 'identite', label: 'Identité', icon: IcBuilding },
    { key: 'tarification', label: 'Tarification', icon: IcPercent },
    { key: 'charges', label: 'Charges fixes', icon: IcBanknote },
  ] },
  { label: 'Catalogue', tabs: [
    // Services déplacé de la navbar vers Paramètres (owner Studio 2026-08-13).
    { key: 'services', label: 'Services', icon: IcSparkles },
    // Catégories produits déplacé du menu Produits vers Paramètres (owner 2026-09-08).
    { key: 'categories-produits', label: 'Catégories produits', icon: IcLayers },
  ] },
  { label: 'Clients', tabs: [
    // Catégories clients déplacé de /clients vers Paramètres (owner Studio 2026-08-16).
    { key: 'categories-clients', label: 'Catégories clients', icon: IcTags },
  ] },
  { label: 'Compte', tabs: [
    // Comptes (ce qui se connecte) et personnes (qui agit) : compte propriétaire seulement.
    // Boutiques (owner 2026-09-28) : chacune avec ses comptes vendeur / manager et ses personnes.
    { key: 'boutiques', label: 'Boutiques', icon: IcMapPin, ownerOnly: true },
    { key: 'utilisateurs', label: 'Comptes', icon: IcUsers, ownerOnly: true },
    { key: 'personnes', label: 'Personnes', icon: IcContact, ownerOnly: true },
    { key: 'deconnexion', label: 'Déconnexion', icon: IcLogOut, danger: true },
  ] },
];
const TAB_KEYS = TAB_GROUPS.flatMap((g) => g.tabs.map((t) => t.key));

// Ligne de lecture (mode verrouillé) : libellé 11px espacé gris, valeur encre.
const ReadRow = ({ label, value, nums }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-0.5 sm:gap-4 py-3 border-b border-gray-100 last:border-b-0">
    <dt className="text-[11px] tracking-[0.8px] uppercase text-gray-400 sm:pt-1">{label}</dt>
    <dd className={`sm:col-span-2 text-sm text-gray-900 whitespace-pre-line break-words ${nums ? 'tabular-nums' : ''}`}>
      {value ? value : <span className="text-gray-300">—</span>}
    </dd>
  </div>
);

// Tuile de valeur (même motif que les tuiles Achats / Score / Points de la carte client).
const Tile = ({ label, value }) => (
  <div className="flex-1 border border-gray-200 rounded-lg p-3 text-center">
    <p className="text-[9.5px] uppercase tracking-[0.8px] text-gray-400">{label}</p>
    <p className="text-xl font-bold text-gray-900 tabular-nums">{value}</p>
  </div>
);

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { settings, refresh } = useCompany();
  // Onglet porté par l'URL (?tab=…) : F5 et liens profonds gardent l'onglet.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab = TAB_KEYS.includes(tabParam) ? tabParam : 'identite';
  const setActiveTab = (key) => setSearchParams(key === 'identite' ? {} : { tab: key }, { replace: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Identité & Tarification tabs are read-only until the user presses « Modifier ».
  const [editing, setEditing] = useState(false);
  const [editingTarif, setEditingTarif] = useState(false);

  const [form, setForm] = useState({
    nom: '', adresse: '', matricule_fiscale: '', telephones: [''], rib: '', bank_name: '', email: '', website: '',
    marge_defaut: 50, tva_defaut: 19,
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Charges state
  const [charges, setCharges] = useState([]);
  const [chargesLoading, setChargesLoading] = useState(false);
  const [newCharge, setNewCharge] = useState({ name: '', type: 'MONTHLY', cost: '' });

  // Load company settings into the form.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = settings || (await companyService.getSettings());
        if (!active) return;
        setForm({
          nom: data.nom || '',
          adresse: data.adresse || '',
          matricule_fiscale: data.matricule_fiscale || '',
          telephones: Array.isArray(data.telephones) && data.telephones.length ? data.telephones : [''],
          rib: data.rib || '',
          bank_name: data.bank_name || '',
          email: data.email || '',
          website: data.website || '',
          marge_defaut: data.marge_defaut != null ? data.marge_defaut : 50,
          tva_defaut: data.tva_defaut != null ? data.tva_defaut : 19,
        });
        if (data.logo_url) setLogoPreview(`${BASE_URL}${data.logo_url}`);
      } catch (e) {
        console.error(e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  // Lazy-load charges when the tab is first opened.
  useEffect(() => {
    if (activeTab !== 'charges') return;
    setChargesLoading(true);
    chargeService.getAll()
      .then(setCharges)
      .catch((e) => console.error(e))
      .finally(() => setChargesLoading(false));
  }, [activeTab]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ---- Multiple phone numbers ----
  const handlePhoneChange = (index, value) => {
    setForm((prev) => {
      const telephones = [...prev.telephones];
      telephones[index] = value;
      return { ...prev, telephones };
    });
  };

  const addPhone = () => {
    setForm((prev) => ({ ...prev, telephones: [...prev.telephones, ''] }));
  };

  const removePhone = (index) => {
    setForm((prev) => {
      const telephones = prev.telephones.filter((_, i) => i !== index);
      return { ...prev, telephones: telephones.length ? telephones : [''] };
    });
  };

  // ---- Identité edit / lock toggle ----
  // Snapshot the values when entering edit mode so « Annuler » can restore them.
  const [snapshot, setSnapshot] = useState(null);

  const startEdit = () => {
    setSnapshot({ form, logoPreview });
    setEditing(true);
  };

  const cancelEdit = () => {
    if (snapshot) {
      setForm(snapshot.form);
      setLogoPreview(snapshot.logoPreview);
    }
    setLogoFile(null);
    setEditing(false);
  };

  // Tarification lock — snapshots only the two pricing fields.
  const [tarifSnapshot, setTarifSnapshot] = useState(null);

  const startEditTarif = () => {
    setTarifSnapshot({ marge_defaut: form.marge_defaut, tva_defaut: form.tva_defaut });
    setEditingTarif(true);
  };

  const cancelEditTarif = () => {
    if (tarifSnapshot) setForm((prev) => ({ ...prev, ...tarifSnapshot }));
    setEditingTarif(false);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const saveCompany = async () => {
    setSaving(true);
    try {
      await companyService.updateSettings({
        nom: form.nom,
        adresse: form.adresse,
        matricule_fiscale: form.matricule_fiscale,
        telephones: form.telephones.map((t) => t.trim()).filter(Boolean),
        rib: form.rib,
        bank_name: form.bank_name,
        email: form.email,
        website: form.website,
        marge_defaut: parseFloat(form.marge_defaut) || 0,
        tva_defaut: parseFloat(form.tva_defaut) || 0,
      });
      if (logoFile) {
        await companyService.uploadLogo(logoFile);
        setLogoFile(null);
      }
      await refresh();
      // Back to the locked / read-only view after a successful save.
      setEditing(false);
      setEditingTarif(false);
      Swal.fire('Enregistré', 'Les paramètres ont été mis à jour.', 'success');
    } catch (e) {
      console.error(e);
      Swal.fire('Erreur', "Impossible d'enregistrer les paramètres.", 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---- Charges handlers ----
  const addCharge = async (e) => {
    e.preventDefault();
    if (!newCharge.name.trim()) return;
    try {
      const created = await chargeService.create({
        name: newCharge.name.trim(),
        description: '',
        type: newCharge.type,
        cost: parseFloat(newCharge.cost) || 0,
      });
      setCharges((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewCharge({ name: '', type: 'MONTHLY', cost: '' });
    } catch (err) {
      console.error(err);
      Swal.fire('Erreur', "Impossible d'ajouter la charge.", 'error');
    }
  };

  const deleteCharge = async (charge) => {
    const res = await Swal.fire({
      title: 'Supprimer ?', text: `Supprimer la charge « ${charge.name} » ?`,
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Supprimer',
      cancelButtonText: 'Annuler', confirmButtonColor: KI.coralDark,
    });
    if (!res.isConfirmed) return;
    try {
      await chargeService.remove(charge.id);
      setCharges((prev) => prev.filter((c) => c.id !== charge.id));
    } catch (err) {
      console.error(err);
      Swal.fire('Erreur', 'Impossible de supprimer la charge.', 'error');
    }
  };

  const handleLogout = async () => {
    const res = await Swal.fire({
      title: 'Déconnexion', text: 'Voulez-vous vous déconnecter ?',
      icon: 'question', showCancelButton: true, confirmButtonText: 'Déconnexion',
      cancelButtonText: 'Annuler', confirmButtonColor: KI.coralDark,
    });
    if (!res.isConfirmed) return;
    try { await authService.logout(); } catch (e) { /* force logout below */ }
    navigate('/login');
  };

  // ---- Derived: price-calc preview for the Tarification tab ----
  const pricePreview = useMemo(() => {
    const cost = 100;
    const tva = (parseFloat(form.tva_defaut) || 0) / 100;
    const marge = (parseFloat(form.marge_defaut) || 0) / 100;
    return round3(cost * (1 + tva) * (1 + marge));
  }, [form.tva_defaut, form.marge_defaut]);

  const chargesTotalMonthly = useMemo(() => {
    const factor = { MONTHLY: 1, TRIMESTER: 1 / 3, SEMESTER: 1 / 6, ANNUAL: 1 / 12 };
    return charges.reduce((sum, c) => sum + (Number(c.cost) || 0) * (factor[c.type] || 1), 0);
  }, [charges]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <CardSkeleton />
      </div>
    );
  }

  const phonesText = form.telephones.map((t) => t.trim()).filter(Boolean).join(' · ');
  const chargeTypeLabel = (type) => (CHARGE_TYPES.find((t) => t.value === type) || {}).label || type;

  return (
    <div className="w-full px-4 pt-2 pb-4">
      {/* Page name as a gray chip — same design as ProductList/CustomerList
          (owner Studio); chip = refresh button. */}
      <div className="mb-6">
        <button type="button" onClick={() => window.location.reload()} title="Actualiser la page" className="btn-s px-4 py-2 rounded-xl text-sm">
          <IcSettings size={13} /> Paramètres
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start md:items-stretch">
        {/* Rail : groupes (libellé 11px espacé gris), onglet actif encre,
            Déconnexion rouge sous un filet ; collant sous le header sur desktop,
            chips défilantes sur mobile. */}
        <nav className="w-full md:w-56 shrink-0">
          {/* Desktop : le rail a la hauteur « Nouvelle vente » (.settings-rail,
              index.css) ; l'espace en trop est réparti À ÉGALITÉ entre les
              quatre groupes (lg:justify-between, owner 2026-10-01 : « pas un
              grand vide sous Clients ») : Compte (Déconnexion) finit en bas du
              rail et chaque groupe finit de la même façon. Le collant est sur
              la boîte (le <nav> est étiré par la rangée). */}
          <div className="settings-rail bg-white rounded-2xl border border-gray-200 p-2 flex gap-1 overflow-x-auto md:flex-col md:gap-0 md:overflow-visible md:sticky md:top-20 lg:justify-between">
            {TAB_GROUPS.map((g, gi) => (
              <div key={g.label} className={`flex gap-1 shrink-0 md:block ${gi > 0 ? 'md:mt-2 md:pt-2 md:border-t md:border-gray-100' : ''}`}>
                <p className="hidden md:block px-3 pt-1.5 pb-1 text-[11px] tracking-[0.8px] uppercase text-gray-400">{g.label}</p>
                {g.tabs.filter((t) => !t.ownerOnly || authService.isOwner()).map((t) => {
                  const Icon = t.icon;
                  const active = activeTab === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setActiveTab(t.key)}
                      aria-current={active ? 'page' : undefined}
                      // Onglet actif = kit « Secondaire — bordé encre » (.btn-s :
                      // blanc, texte + bordure encre 1px, rayon 6), icône en encre
                      // (sélection Studio owner 2026-09-09). Bordure transparente
                      // sur les inactifs pour garder la même hauteur.
                      className={`shrink-0 md:w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md border text-sm whitespace-nowrap transition-colors ${
                        active
                          ? 'bg-white text-gray-900 font-semibold border-gray-900 hover:bg-gray-100'
                          : t.danger
                            ? 'border-transparent text-red-600 hover:bg-red-50 font-medium'
                            : 'border-transparent text-gray-700 hover:bg-gray-100 font-medium'
                      }`}
                    >
                      <Icon size={15} className={active ? 'text-gray-900' : t.danger ? 'text-red-500' : 'text-gray-400'} />
                      <span>{t.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </nav>

        {/* Contenu : même hauteur que le rail (.settings-body, index.css) ; les
            grilles de cartes sont `flex-1` pour que les cartes s'étirent
            jusqu'à la ligne de Déconnexion ; une liste longue défile dans la
            carte. */}
        <div className="settings-body flex-1 min-w-0 w-full">
          {/* ---------------- Identité ---------------- */}
          {activeTab === 'identite' && (
            <div className="flex-1 grid grid-cols-1 xl:grid-cols-5 gap-6">
              <SectionCard
                className="xl:col-span-3"
                title="Identité de l'entreprise"
                sub="Coordonnées imprimées sur les factures et les tickets."
                action={!editing && (
                  <button type="button" onClick={startEdit} className="btn-g-edit shrink-0 gap-2 px-4 py-2 text-sm transition">
                    <IcPencil /> Modifier
                  </button>
                )}
                footer={editing && (
                  <>
                    <button type="button" onClick={cancelEdit} disabled={saving} className="btn-g px-5 py-2.5 transition">Annuler</button>
                    <button type="button" onClick={saveCompany} disabled={saving} className="btn-p gap-2 px-5 py-2.5 transition">
                      <IcSave /> {saving ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                  </>
                )}
              >
                {!editing ? (
                  <>
                    {/* Bloc identité : logo + nom */}
                    <div className="flex items-center gap-4 pb-4 border-b border-gray-100">
                      {logoPreview ? (
                        <img src={logoPreview} alt="logo" className="h-14 w-14 object-contain rounded-lg border border-gray-200 bg-white" />
                      ) : (
                        <div className="h-14 w-14 rounded-lg bg-gray-100 flex items-center justify-center text-gray-300"><IcImage /></div>
                      )}
                      <div className="min-w-0">
                        <p className="text-lg font-semibold text-gray-900 truncate">{form.nom || 'Kipara'}</p>
                        <p className="text-xs text-gray-400">{form.matricule_fiscale ? `MF ${form.matricule_fiscale}` : 'Matricule fiscale non renseigné'}</p>
                      </div>
                    </div>
                    <dl>
                      <ReadRow label="Adresse" value={form.adresse} />
                      <ReadRow label="Téléphone(s)" value={phonesText} nums />
                      <ReadRow label="Email" value={form.email} />
                      <ReadRow label="Site web" value={form.website} />
                      <ReadRow label="RIB" value={form.rib} nums />
                      <ReadRow label="Banque" value={form.bank_name} />
                    </dl>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>Nom de la parapharmacie</label>
                      <input className={inputClass} name="nom" value={form.nom} onChange={handleChange} placeholder="Kipara" />
                    </div>
                    <div>
                      <label className={labelClass}>Adresse</label>
                      <textarea className={inputClass} name="adresse" rows={2} value={form.adresse} onChange={handleChange} placeholder="Rue, ville, code postal" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Matricule fiscale</label>
                        <input className={inputClass} name="matricule_fiscale" value={form.matricule_fiscale} onChange={handleChange} placeholder="0000000/A/M/000" />
                      </div>
                      <div>
                        <label className={labelClass}>Email</label>
                        <input className={inputClass} name="email" type="email" value={form.email} onChange={handleChange} placeholder="contact@kipara.store" />
                      </div>
                    </div>

                    {/* Téléphones — un ou plusieurs numéros */}
                    <div>
                      <label className={labelClass}>Téléphone(s)</label>
                      <div className="space-y-2">
                        {form.telephones.map((phone, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <input className={inputClass} value={phone} onChange={(e) => handlePhoneChange(index, e.target.value)} placeholder="+216 ..." />
                            <button
                              type="button"
                              onClick={() => removePhone(index)}
                              disabled={form.telephones.length === 1 && !phone}
                              className="shrink-0 h-9 w-9 flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent transition"
                              title="Supprimer ce numéro"
                              aria-label="Supprimer ce numéro"
                            >
                              <IcTrash />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={addPhone} className="btn-add-link mt-2 gap-1.5 text-sm">
                        <IcPlus className="text-xs" /> Ajouter un numéro
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>RIB</label>
                        <input className={inputClass} name="rib" value={form.rib} onChange={handleChange} placeholder="00 000 0000000000000 00" />
                      </div>
                      <div>
                        <label className={labelClass}>Nom de la banque</label>
                        <input className={inputClass} name="bank_name" value={form.bank_name} onChange={handleChange} placeholder="Ex: BIAT, Attijari…" />
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Site web</label>
                      <input className={inputClass} name="website" value={form.website} onChange={handleChange} placeholder="https://www.kipara.store" />
                    </div>
                    <div>
                      <label className={labelClass}>Logo</label>
                      <label className="flex items-center gap-3 px-4 py-3 border border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition">
                        {logoPreview ? (
                          <img src={logoPreview} alt="logo" className="h-9 w-9 object-contain rounded" />
                        ) : (
                          <IcImage className="text-gray-400" />
                        )}
                        <span className="text-sm text-gray-500">{logoFile ? logoFile.name : 'Choisir une image…'}</span>
                        <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                      </label>
                    </div>
                  </div>
                )}
              </SectionCard>

              {/* Aperçu en-tête facture */}
              <SectionCard className="xl:col-span-2" title="Aperçu en-tête facture" sub="Tel qu'il apparaîtra sur vos documents.">
                <div className="flex items-start justify-between gap-4 border-b border-gray-200 pb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    {logoPreview ? (
                      <img src={logoPreview} alt="logo" className="h-12 w-12 object-contain rounded" />
                    ) : (
                      <div className="h-12 w-12 rounded bg-gray-100 flex items-center justify-center text-gray-300"><IcImage /></div>
                    )}
                    <div className="min-w-0">
                      <div className="text-lg font-bold text-gray-900 truncate">{form.nom || 'Kipara'}</div>
                      {form.adresse && <div className="text-xs text-gray-500 whitespace-pre-line">{form.adresse}</div>}
                    </div>
                  </div>
                  <div className="text-right text-xs text-gray-500 space-y-0.5 tabular-nums shrink-0">
                    {form.matricule_fiscale && <div><span className="text-gray-400">MF</span> {form.matricule_fiscale}</div>}
                    {form.telephones.filter((t) => t.trim()).map((t, i) => (
                      <div key={i}><span className="text-gray-400">Tél</span> {t}</div>
                    ))}
                    {form.email && <div>{form.email}</div>}
                    {form.website && <div>{form.website}</div>}
                    {form.rib && <div><span className="text-gray-400">RIB</span> {form.rib}</div>}
                    {form.bank_name && <div><span className="text-gray-400">Banque</span> {form.bank_name}</div>}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <div className="text-base font-semibold text-gray-900">FACTURE</div>
                  <div className="text-xs text-gray-400 tabular-nums">N° 0001 — 26/06/2026</div>
                </div>
                <div className="mt-3 h-20 rounded-lg bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-300">
                  (lignes de la facture)
                </div>
              </SectionCard>
            </div>
          )}

          {/* ---------------- Tarification ---------------- */}
          {activeTab === 'tarification' && (
            <div className="flex-1 grid grid-cols-1 xl:grid-cols-5 gap-6">
              <SectionCard
                className="xl:col-span-3"
                title="Tarification"
                sub="Valeurs par défaut utilisées pour calculer le prix de vente d'un produit."
                action={!editingTarif && (
                  <button type="button" onClick={startEditTarif} className="btn-g-edit shrink-0 gap-2 px-4 py-2 text-sm transition">
                    <IcPencil /> Modifier
                  </button>
                )}
                footer={editingTarif && (
                  <>
                    <button type="button" onClick={cancelEditTarif} disabled={saving} className="btn-g px-5 py-2.5 transition">Annuler</button>
                    <button type="button" onClick={saveCompany} disabled={saving} className="btn-p gap-2 px-5 py-2.5 transition">
                      <IcSave /> {saving ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                  </>
                )}
              >
                {!editingTarif ? (
                  <div className="flex gap-3">
                    <Tile label="Marge par défaut" value={`${form.marge_defaut || 0} %`} />
                    <Tile label="TVA par défaut" value={`${form.tva_defaut || 0} %`} />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Marge par défaut (%)</label>
                      <input className={inputClass} name="marge_defaut" type="number" step="0.1" value={form.marge_defaut} onChange={handleChange} />
                    </div>
                    <div>
                      <label className={labelClass}>TVA par défaut (%)</label>
                      <input className={inputClass} name="tva_defaut" type="number" step="0.1" value={form.tva_defaut} onChange={handleChange} />
                    </div>
                  </div>
                )}
              </SectionCard>

              <SectionCard className="xl:col-span-2" title="Exemple de calcul" sub="Pour un coût d'achat de 100,000 DT HT.">
                <div className="space-y-2 text-sm tabular-nums">
                  <div className="flex justify-between"><span className="text-gray-500">Coût d'achat (HT)</span><span className="text-gray-900">100,000</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">+ TVA {form.tva_defaut || 0} %</span><span className="text-gray-900">{round3(100 * ((parseFloat(form.tva_defaut) || 0) / 100)).toFixed(3)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">+ Marge {form.marge_defaut || 0} %</span><span className="text-gray-900">{round3((pricePreview) - 100 * (1 + (parseFloat(form.tva_defaut) || 0) / 100)).toFixed(3)}</span></div>
                  <div className="flex justify-between items-baseline border-t border-gray-200 pt-3 mt-3">
                    <span className="font-semibold text-gray-900">Prix de vente</span>
                    <span className="text-lg font-bold text-gray-900">{pricePreview.toFixed(3)} DT</span>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-4">Prix de vente = (Coût HT + TVA) × (1 + marge).</p>
              </SectionCard>
            </div>
          )}

          {/* ---------------- Charges fixes ---------------- */}
          {activeTab === 'charges' && (
            <SectionCard
              className="flex-1"
              title="Charges fixes"
              sub="Personnel, loyer et autres charges récurrentes."
              action={(
                <div className="text-right shrink-0">
                  <p className="text-[11px] tracking-[0.8px] uppercase text-gray-400">Total / mois</p>
                  <p className="text-lg font-bold tabular-nums text-gray-900">{chargesTotalMonthly.toFixed(3)} DT</p>
                </div>
              )}
            >
              <form onSubmit={addCharge} className="grid grid-cols-1 sm:grid-cols-[1fr_11rem_9rem_auto] gap-2 mb-5">
                <input
                  className={inputClass} placeholder="Nom (ex : Loyer)" aria-label="Nom de la charge"
                  value={newCharge.name} onChange={(e) => setNewCharge((p) => ({ ...p, name: e.target.value }))}
                />
                <select
                  className={inputClass} value={newCharge.type} aria-label="Périodicité"
                  onChange={(e) => setNewCharge((p) => ({ ...p, type: e.target.value }))}
                >
                  {CHARGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input
                  className={inputClass} type="number" step="0.001" placeholder="Coût (DT)" aria-label="Coût"
                  value={newCharge.cost} onChange={(e) => setNewCharge((p) => ({ ...p, cost: e.target.value }))}
                />
                <button type="submit" className="btn-add-block gap-2 px-4 py-2.5 rounded-md transition">
                  <IcPlus /> Ajouter
                </button>
              </form>

              {chargesLoading ? (
                <TableSkeleton rows={5} columns={[
    { label: 'Charge', w: 300 },
    { label: 'Périodicité', w: 80 },
    { label: 'Coût', align: 'right', w: 72 },
    { label: 'Actions', align: 'right', w: 60 },
  ]} />
              ) : charges.length === 0 ? (
                <div className="empty-ki p-8">
                  <IcBanknote size={22} className="empty-ki-icon" />
                  <p className="empty-ki-title">Aucune charge fixe</p>
                  <p className="empty-ki-sub mb-0">Ajoutez le loyer, les salaires ou toute charge récurrente.</p>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-6 -mb-6 border-t border-gray-100">
                  <table className="min-w-full">
                    <thead>
                      <tr>
                        <th scope="col" className="th-ki px-6 py-3 text-left uppercase">Charge</th>
                        <th scope="col" className="th-ki px-6 py-3 text-left uppercase w-40">Périodicité</th>
                        <th scope="col" className="th-ki px-6 py-3 text-right uppercase w-40">Coût</th>
                        <th scope="col" className="th-ki px-6 py-3 w-16"><span className="sr-only">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {charges.map((c) => (
                        <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-100 transition-colors">
                          <td className="td-ki px-6 py-3 text-sm font-semibold text-gray-900">{c.name}</td>
                          <td className="td-ki px-6 py-3 text-sm"><span className="bdg bdg-neutral">{chargeTypeLabel(c.type)}</span></td>
                          <td className="td-ki px-6 py-3 text-sm text-right font-semibold tabular-nums text-gray-900">{Number(c.cost).toFixed(3)} DT</td>
                          <td className="td-ki px-6 py-3 text-right">
                            <button type="button" onClick={() => deleteCharge(c)} className="h-9 w-9 inline-flex items-center justify-center rounded-lg text-red-600 hover:bg-red-50 transition-colors" title="Supprimer" aria-label="Supprimer la charge">
                              <IcTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          )}

          {/* ---------------- Pages intégrées ---------------- */}
          {activeTab === 'services' && (
            <div className="settings-embed"><ServicesList embedded /></div>
          )}
          {activeTab === 'categories-clients' && (
            <div className="settings-embed"><ClientCategoryManagement embedded /></div>
          )}
          {activeTab === 'categories-produits' && (
            <div className="settings-embed"><CategoryList embedded /></div>
          )}
          {activeTab === 'boutiques' && authService.isOwner() && (
            <div className="settings-embed"><BoutiquesList /></div>
          )}
          {activeTab === 'utilisateurs' && authService.isOwner() && (
            <div className="settings-embed"><UserList embedded /></div>
          )}
          {activeTab === 'personnes' && authService.isOwner() && (
            <div className="settings-embed"><PeopleList /></div>
          )}

          {/* ---------------- Déconnexion ---------------- */}
          {activeTab === 'deconnexion' && (
            <SectionCard className="flex-1" title="Déconnexion" sub="Fermer votre session et revenir à l'écran de connexion.">
              <button type="button" onClick={handleLogout} className="btn-db gap-2 px-5 py-2.5 transition">
                <IcLogOut /> Se déconnecter
              </button>
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
